import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import React from "react";
import { PlayerMatch } from "../../interfaces";
import { Player } from "../../interfaces/Team";
import { LegCell, PlayerCell } from "../Cells";
import PlayerSelect from "../PlayerSelect";
import LegInputs from "./LegInputs";
import { useMatchDetailsContext } from "./MatchDetailsContext";

interface SinglesTableBodyProps {
  playerMatches: PlayerMatch[];
  homePlayers: Player[];
  awayPlayers: Player[];
  homeInputs: { [matchId: number]: string[] };
  awayInputs: { [matchId: number]: string[] };
  handleSinglePlayerChange: (
    matchId: number,
    type: "home" | "away",
    playerId: number
  ) => void;
  handleInputChange: (
    type: "home" | "away",
    matchId: number,
    idx: number,
    value: string
  ) => void;
  handleInputBlur: (
    type: "home" | "away",
    matchId: number,
    idx: number
  ) => void;
  getRowProps?: (pm: any) => any;
  usedHomeSingleIds?: number[];
  usedAwaySingleIds?: number[];
  hoveredRow: number | null;
  setHoveredRow: React.Dispatch<React.SetStateAction<number | null>>;
  isAdmin: boolean;
}

const ROW_HEIGHT = 40;

const SinglesTableBody: React.FC<SinglesTableBodyProps> = ({
  playerMatches,
  homePlayers,
  awayPlayers,
  homeInputs,
  awayInputs,
  handleSinglePlayerChange,
  handleInputChange,
  handleInputBlur,
  usedHomeSingleIds,
  usedAwaySingleIds,
  setHoveredRow,
  isAdmin,
}) => {
  const { mistakes } = useMatchDetailsContext();

  return (
    <>
      <TableBody>
        {playerMatches.map((pm, matchIdx) => {
          const homeVals = homeInputs[pm.id] || ["", "", ""];
          const awayVals = awayInputs[pm.id] || ["", "", ""];

          const winCountHome = homeVals.filter(
            (v) => v && !isNaN(Number(v))
          ).length;
          const winCountAway = awayVals.filter(
            (v) => v && !isNaN(Number(v))
          ).length;
          // Prepare player label with win count for home
          const homePlayersWithCount = homePlayers.map((p) => ({
            ...p,
            displayName:
              p.name +
              (pm.home_player && p.id === pm.home_player.id && winCountHome > 0
                ? ` (${winCountHome})`
                : ""),
          }));
          // Prepare player label with win count for away
          const awayPlayersWithCount = awayPlayers.map((p) => ({
            ...p,
            displayName:
              p.name +
              (pm.away_player && p.id === pm.away_player.id && winCountAway > 0
                ? ` (${winCountAway})`
                : ""),
          }));
          const mistake = mistakes.find((m) => m.id === pm.id);
          return (
            <React.Fragment key={pm.id}>
              <TableRow
                hover
                onMouseEnter={() => setHoveredRow(pm.id)}
                onMouseLeave={() =>
                  setHoveredRow((prev) => (prev === pm.id ? null : prev))
                }
                sx={{
                  height: ROW_HEIGHT,
                  minHeight: ROW_HEIGHT,
                  maxHeight: ROW_HEIGHT,
                  borderLeft: mistake ? "8px solid red" : "",
                }}
              >
                <PlayerCell>
                  <PlayerSelect
                    matchId={pm.id}
                    direction="ltr"
                    isAdmin={isAdmin}
                    players={homePlayersWithCount}
                    value={pm.home_player ? pm.home_player.id : ""}
                    label={undefined}
                    placeholder="Heimspieler wählen"
                    onChange={(e) => {
                      const newId = Number(e.target.value);
                      handleSinglePlayerChange(pm.id, "home", newId);
                    }}
                    inputHeight={ROW_HEIGHT}
                    // Disable options for players already selected in another single
                    disabledOptions={usedHomeSingleIds?.filter(
                      (id) => id !== pm.home_player?.id
                    )}
                  />
                </PlayerCell>

                <LegCell hasBorderRight>
                  <LegInputs
                    isAdmin={isAdmin}
                    matchId={pm.id}
                    type="home"
                    values={homeVals}
                    onChange={(idx, value) =>
                      handleInputChange("home", pm.id, idx, value)
                    }
                    onBlur={(idx) => handleInputBlur("home", pm.id, idx)}
                    tabIndexBase={matchIdx * 6 + 1}
                    idPrefix="home"
                    inputHeight={ROW_HEIGHT}
                  />
                </LegCell>
                <LegCell>
                  <LegInputs
                    isAdmin={isAdmin}
                    matchId={pm.id}
                    type="away"
                    values={awayVals}
                    onChange={(idx, value) =>
                      handleInputChange("away", pm.id, idx, value)
                    }
                    onBlur={(idx) => handleInputBlur("away", pm.id, idx)}
                    tabIndexBase={matchIdx * 6 + 4}
                    idPrefix="away"
                    inputHeight={ROW_HEIGHT}
                  />
                </LegCell>

                <PlayerCell>
                  <PlayerSelect
                    matchId={pm.id}
                    direction="rtl"
                    isAdmin={isAdmin}
                    players={awayPlayersWithCount}
                    value={pm.away_player ? pm.away_player.id : ""}
                    label={undefined}
                    placeholder="Auswärtsspieler wählen"
                    onChange={(e) => {
                      const newId = Number(e.target.value);
                      handleSinglePlayerChange(pm.id, "away", newId);
                    }}
                    inputHeight={ROW_HEIGHT}
                    // Disable options for players already selected in another single
                    disabledOptions={usedAwaySingleIds?.filter(
                      (id) => id !== pm.away_player?.id
                    )}
                  />
                </PlayerCell>
              </TableRow>
            </React.Fragment>
          );
        })}
      </TableBody>
    </>
  );
};

export default SinglesTableBody;
